// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Box, Stack } from '@mantine/core';

import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { ModalButtons } from 'components/Mantine/Modals/ModalButtons';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';

import classes from './PrintableModal.module.css';

type Props = ModalRegistryManagedProps & {
  /** Modal title. Hidden when printing, along with everything else outside `children`. */
  title: string;
  /** The printable document. Everything outside it is hidden when the browser prints. */
  children: ReactNode;
};

/**
 * A modal whose body is a print preview — the app, the modal chrome and the footer are all
 * hidden when the browser prints, leaving just `children` on the page. The footer offers Cancel
 * and a Print button wired to the browser's own print workflow.
 *
 * Long content paginates: table headers repeat per page and rows are never split across a page
 * boundary. Wrap a table in `PrintableTableFrame` to give it a bordered scroll area on screen.
 */
export const PrintableModal = ({ title, onClose, children, ...props }: Props) => (
  <WrappedModal
    onClose={onClose}
    onConfirm={onClose}
    title={title}
    size="xl"
    hideModalButtons
    tall
    bodyProps={{ p: 0 }}
    {...props}
  >
    <Stack h="100%" className={classes.printableModal} p="xs">
      <Box className={classes.printContent}>{children}</Box>
      {/* ModalButtons is rendered here rather than by WrappedModal so that it sits inside the root element,
          where the print rule that hides everything but .printContent can reach it. */}
      <ModalButtons confirmationText="Print" onConfirm={() => window.print()} onClose={onClose} />
    </Stack>
  </WrappedModal>
);

/**
 * Gives a table a bordered, scrollable area on screen while leaving it free to paginate when
 * printed. Use inside `PrintableModal` for content tall enough to need its own scrollbar.
 *
 * Only for a modal whose body is essentially one table. A document built from several stacked
 * blocks should let `.printContent` do the scrolling rather than nest a scroll area inside it.
 */
export const PrintableTableFrame = ({ children }: { children: ReactNode }) => (
  <Box className={classes.tableContainer}>
    <Box className={classes.tableScroll}>{children}</Box>
  </Box>
);

/** Keeps its children on one printed page instead of letting them split across a page break. */
export const PrintableBlock = ({ children }: { children: ReactNode }) => (
  <Box className={classes.printableBlock}>{children}</Box>
);
